import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Eye, EyeOff } from 'lucide-react';

// Lead-native auth — POTPUNO odvojen od CRM-a. Vlastiti /api/auth/login (Lead backend) protiv
// crm_users na Lead bazi. Bez api.leoplatform.eu (IP-approval / concurrent-session = bivši "loop").
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function LoginPage() {
  const { loginWithToken } = useAuth();
  const [step, setStep] = useState('email'); // email | password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [idleNotice, setIdleNotice] = useState(false);

  // Poruka ako je korisnik automatski odjavljen zbog neaktivnosti (30 min)
  useEffect(() => {
    try {
      if (localStorage.getItem('logoutReason') === 'inactivity') {
        setIdleNotice(true);
        localStorage.removeItem('logoutReason');
      }
    } catch {}
  }, []);

  // ── Korak 1: email → idi na lozinku (nema CRM preflight/IP-approval) ──
  const submitEmail = (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setError(''); setStep('password');
  };

  // ── Korak 2: lozinka → Lead-native login (vlastiti backend, crm_users na Lead bazi) ──
  const submitPassword = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setError(''); setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email.trim(), password }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 200 && d.token) { await finish(d); return; }
      const det = d.detail;
      // 403 LOCATION_PENDING — prijava s nove IP čeka admin odobrenje (Telegram)
      if (r.status === 403 && det && typeof det === 'object' && det.code === 'LOCATION_PENDING') {
        setError('🔐 ' + (det.message || 'Vaša lokacija čeka odobrenje administratora. Pokušajte ponovo nakon potvrde.'));
      } else {
        // detail može biti string (401) ili objekt — izvuci poruku da ne sruši render
        const msg = (det && typeof det === 'object') ? (det.message || 'Prijava odbijena.')
                    : (det || d.error || 'Pogrešan e-mail ili lozinka.');
        setError(msg);
      }
    } catch {
      setError('Greška u povezivanju. Pokušajte ponovo.');
    } finally { setLoading(false); }
  };

  const finish = async (d) => {
    await loginWithToken(d.token, d.user);
    // AuthContext setUser → ProtectedRoute propušta na dashboard
  };

  // ── stilovi ──
  const card = { background: '#FFFFFF', border: '1px solid #E8E5E0', borderRadius: '16px', boxShadow: '0 4px 24px rgba(120,100,60,0.08)', padding: '40px 36px', textAlign: 'center' };
  const inp = { width: '100%', padding: '11px 14px', fontSize: '14px', fontFamily: 'inherit', color: '#1C1917', background: '#FAFAF8', border: '1px solid #E8E5E0', borderRadius: '8px', outline: 'none' };
  const btn = { width: '100%', padding: '12px 24px', background: 'linear-gradient(135deg, #C17F0A 0%, #A86D00 100%)', color: '#FFFFFF', fontSize: '15px', fontWeight: 700, border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(193,127,10,0.25)', marginTop: '8px' };
  const ghost = { width: '100%', padding: '10px', marginTop: '10px', background: 'none', color: '#A8A29E', border: '1px solid #E8E5E0', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#1C1917', marginBottom: '6px', textAlign: 'left' };

  const dots = (idx) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: '24px' }}>
      {[0, 1].map(i => (
        <React.Fragment key={i}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: i <= idx ? '#C17F0A' : '#E8E5E0', transform: i === idx ? 'scale(1.3)' : 'none', transition: 'all 0.25s' }} />
          {i < 1 && <div style={{ width: '28px', height: '2px', background: i < idx ? '#C17F0A' : '#E8E5E0' }} />}
        </React.Fragment>
      ))}
    </div>
  );

  const stepIdx = { email: 0, password: 1 }[step] || 0;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#FAFAF8' }}>
      <div style={{ width: '100%', maxWidth: '420px', padding: '20px' }}>
        <div style={card}>
          <div style={{ width: '56px', height: '56px', margin: '0 auto 20px', background: 'linear-gradient(135deg, #C17F0A 0%, #F5A623 50%, #C17F0A 100%)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(193,127,10,0.2)' }}>
            <Sparkles size={28} color="#FFFFFF" />
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#1C1917', letterSpacing: '-0.02em', marginBottom: '4px' }}>
            People <span style={{ color: '#C17F0A' }}>Pons</span>
          </h1>
          <p style={{ fontSize: '13px', color: '#57534E', marginBottom: '24px' }}>Lead Intelligence — prijava</p>

          {dots(stepIdx)}

          {idleNotice && !error && (
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px' }}>
              Odjavljeni ste zbog 30 min neaktivnosti. Prijavite se ponovno.
            </div>
          )}

          {error && (
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#DC2626', background: '#FEE2E2', border: '1px solid #FECACA', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Korak 1: Email */}
          {step === 'email' && (
            <form onSubmit={submitEmail}>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>E-mail adresa</label>
                <input type="email" autoFocus value={email} onChange={e => setEmail(e.target.value)} placeholder="vas@email.com" style={inp} />
              </div>
              <button type="submit" disabled={loading} style={{ ...btn, opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Provjera...' : 'Dalje →'}
              </button>
            </form>
          )}

          {/* Korak 2: Lozinka */}
          {step === 'password' && (
            <form onSubmit={submitPassword}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#F5F0E8', border: '1px solid #E8E5E0', borderRadius: '20px', padding: '4px 12px', fontSize: '12.5px', color: '#57534E', marginBottom: '16px' }}>
                📧 {email}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>Lozinka</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} autoFocus value={password} onChange={e => setPassword(e.target.value)} placeholder="Unesite lozinku" style={{ ...inp, paddingRight: '44px' }} />
                  <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#A8A29E', padding: '4px', display: 'flex' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} style={{ ...btn, opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Prijava...' : 'Dalje →'}
              </button>
              <button type="button" onClick={() => { setStep('email'); setPassword(''); }} style={ghost}>← Natrag</button>
            </form>
          )}

        </div>
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#A8A29E', marginTop: '16px' }}>
          People Pons &copy; 2026 · Lead Intelligence
        </p>
      </div>
    </div>
  );
}
